import config from '../config';

class ApiClient {
    constructor() {
        this.baseUrl = config.API_BASE_URL;
    }

    async getAuthHeaders(getSessionToken) {
        try {
            // Get fresh token from Clerk on each request
            const token = await getSessionToken();
            if (!token) {
                // Don't throw error here, just return headers without auth
                // This allows optional auth endpoints to work
                console.log('No auth token available, proceeding without authentication');
                return {
                    'Content-Type': 'application/json'
                };
            }
            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
        } catch (error) {
            console.error('Failed to get auth token:', error);
            throw new Error('Authentication failed. Please sign in again.');
        }
    }

    async handleResponse(response, endpoint) {
        if (!response.ok) {
            // Handle specific error codes
            if (response.status === 401) {
                // Token expired or invalid
                throw new Error('Session expired. Please sign in again.');
            } else if (response.status === 403) {
                throw new Error('Access denied. You do not have permission to perform this action.');
            } else if (response.status === 404) {
                throw new Error(`Resource not found: ${endpoint}`);
            } else if (response.status >= 500) {
                throw new Error('Server error. Please try again later.');
            } else {
                const errorText = await response.text().catch(() => response.statusText);
                throw new Error(`API request failed: ${errorText}`);
            }
        }
        return response;
    }

    async retryWithFreshToken(fn, getSessionToken, maxRetries = 1) {
        let lastError;
        
        for (let i = 0; i <= maxRetries; i++) {
            try {
                return await fn();
            } catch (error) {
                lastError = error;
                
                // Only retry on authentication errors
                if (i < maxRetries && error.message.includes('Session expired')) {
                    console.log('Token expired, retrying with fresh token...');
                    // Small delay before retry
                    await new Promise(resolve => setTimeout(resolve, 100));
                    continue;
                }
                
                throw error;
            }
        }
        
        throw lastError;
    }

    async get(endpoint, params = {}, getSessionToken) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);
            const queryString = new URLSearchParams(params).toString();
            const url = `${this.baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers
            });

            await this.handleResponse(response, endpoint);
            return await response.json();
        }, getSessionToken);
    }

    async post(endpoint, data = {}, getSessionToken, options = {}) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);
            
            // Handle FormData for file uploads
            if (data instanceof FormData) {
                delete headers['Content-Type']; // Let browser set boundary for multipart
            }

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                headers,
                body: data instanceof FormData ? data : JSON.stringify(data),
                ...options
            });

            await this.handleResponse(response, endpoint);
            return await response.json();
        }, getSessionToken);
    }


    async put(endpoint, data = {}, getSessionToken) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);
            
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data)
            });

            await this.handleResponse(response, endpoint);
            return await response.json();
        }, getSessionToken);
    }

    async delete(endpoint, getSessionToken) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);
            
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'DELETE',
                headers
            });

            await this.handleResponse(response, endpoint);
            return await response.json();
        }, getSessionToken);
    }

    // For downloads that return blobs
    async download(endpoint, params = {}, getSessionToken) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);
            const queryString = new URLSearchParams(params).toString();
            const url = `${this.baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`;

            const response = await fetch(url, {
                method: 'GET',
                headers
            });

            await this.handleResponse(response, endpoint);
            return await response.blob();
        }, getSessionToken);
    }

    async patch(endpoint, data = {}, getSessionToken) {
        return this.retryWithFreshToken(async () => {
            const headers = await this.getAuthHeaders(getSessionToken);

            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(data)
            });

            await this.handleResponse(response, endpoint);
            return await response.json();
        }, getSessionToken);
    }
}

const apiClient = new ApiClient();
export default apiClient;